import { Link, usePage } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode, websiteNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"

type PostCard = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  author: string | null
}

export default function BlogTag({
  tag,
  posts,
  noindex = false,
}: {
  tag: string
  posts: PostCard[]
  // Set by the controller when the tag has too few posts to be a real topic
  // hub. Such a page is a near-duplicate of the single post it lists.
  noindex?: boolean
}) {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const title = `${cap(tag)} — FullCast podcast insights`
  const description = `Articles on ${tag} from FullCast: podcast strategy, production, and relationship-driven growth for established experts.`

  return (
    <MarketingLayout>
      <SeoHead
        title={title}
        description={description}
        noindex={noindex}
        jsonLd={[organizationNode(base), websiteNode(base)]}
      />

      <Section>
        <Link
          href="/blog"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ChevronLeft className="h-4 w-4" />
          All posts
        </Link>

        <Eyebrow>Topic</Eyebrow>
        <h1 className="display-2 mt-4">{cap(tag)}</h1>
        <p className="mt-5 max-w-2xl text-lg text-ink-body">
          Everything we've written on {tag}.
        </p>

        {posts.length === 0 ? (
          <p className="mt-12 text-ink-muted">No posts in this topic yet.</p>
        ) : (
          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {posts.map((post) => (
              <article key={post.id} className="group">
                <Link href={`/blog/${post.slug}`} className="block no-underline">
                  <div className="aspect-[16/9] overflow-hidden rounded-xl border border-hairline bg-surface">
                    {post.cover_image_url ? (
                      <img
                        src={post.cover_image_url}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-ink-muted">
                        <span className="font-display text-sm">FullCast</span>
                      </div>
                    )}
                  </div>
                  <h3 className="mt-4 transition-colors group-hover:text-accent-display">
                    {post.title}
                  </h3>
                  {post.excerpt && <p className="mt-2 text-ink-muted">{post.excerpt}</p>}
                  {post.author && <p className="mt-3 text-sm text-ink-muted">{post.author}</p>}
                </Link>
              </article>
            ))}
          </div>
        )}
      </Section>
    </MarketingLayout>
  )
}

function cap(s: string) {
  return s.replace(/\b\w/g, (c) => c.toUpperCase())
}
